import Handlebars from '@sparta.global.vendors/handlebars';
import { svgHelper } from '@sparta.global.helpers/svg-helper/dist/es6.js';
import { badgingHelper } from '@sparta.global.helpers/badging-helper/dist/es6.js';

/* eslint-disable no-param-reassign */

/* Button Helper */
const UTILITY_NAME = '@sparta.global.helpers/button-helper';
const VALID_BREAKPOINT_KEYS = ['small-up', 'medium-up', 'large-up', 'small-only', 'medium-only', 'large-only'];
const VALID_TAGS = ['button', 'a'];
const DEFAULT_TAG = 'button';
const DEFAULT_BASE_CLASS = 'spa-btn';
const VALID_TYPE_ATTRIBUTES = ['button', 'submit', 'reset'];
const VALID_BTN_TEXT_ICON_MODIFIERS = ['small', 'large', 'disabled'];
const REQUIRED_BUTTON_ATTRIBUTE_KEYS = ['type'];
const ACTION_MODIFIERS = {
  layer: 'spa-ui-layer-link spa-fn',
  popup: 'spa-fn spa-boa-window'
};
const ACTION_LAYER_MODIFIERS = {
  interstitialType: 'spa-ui-layer-interstitial',
  inputHelpLinkType: 'toggle-trigger-link',
  inputHelpIconType: 'toggle-trigger svg-button',
  layerType: 'spa-ui-layer-tooltip-link'
};

/**
 * Checks for object type
 * @param {object} data Input data
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function isObject(data) {
  const result = data !== null && typeof data !== 'undefined' && typeof data === 'object';
  return result;
}

// generates a button from a JCR config
function button(config, secureHost) {
  if (!config) {
    const msg = `Error in ${UTILITY_NAME}: 'button' HBS helper requires a config object.
      Please check that each object in the HBS template exists in your JCR file.
      If you are using multiple languages ensure all language-based JCRs have the same named objects.`.replace(/\s+/g, ' ');
    throw new Error(msg);
  }

  // determine the base class
  // note: it's important to allow an empty jcr value "" to be considered a valid base value
  //  in order to override the default 'spa-btn' base class with NO base class (mainly for links)
  const baseExists = Object.prototype.hasOwnProperty.call(config, 'base');
  const baseIsValid = baseExists && (config.base === '' || config.base.length);
  const BASE_CLASS = baseIsValid ? config.base : DEFAULT_BASE_CLASS;
  const BTN_PREFIX = `${BASE_CLASS}--`;
  const ADDITIONAL_CLASSES = config.classes || '';
  let modifierClasses = '';
  let attributes = '';
  let content = config.content || '';
  let EXCLUDE_ROLE_ATTRIBUTE_KEY = [];
  let badging = '';
  let ariaPressed = '';
  let contentLeft = '';
  let contentRight = '';
  const BTN_TEXT_ICON = `${BASE_CLASS}-icon`;

  // determine element tag (or default to 'button' tag)
  const TAG = VALID_TAGS.indexOf(config.tag) !== -1 ? config.tag : DEFAULT_TAG;
  const isButton = TAG === 'button';
  const EXISTING_ATTRIBUTE_KEYS = config.attributes ? Object.keys(config.attributes) : [];
  const REQUIRED_ATTRIBUTE_KEYS = isButton ? REQUIRED_BUTTON_ATTRIBUTE_KEYS : [];
  const isTextIcon = config.iconTextPattern || '';

  // convert keywords to class names
  // (for example: the keyword 'primary' will convert to the full 'spa-btn--primary' class name)
  if (config.modifiers) {
    config.modifiers.split(' ').forEach(keyword => {
      if (!isTextIcon || VALID_BTN_TEXT_ICON_MODIFIERS.indexOf(keyword) !== -1) {
        modifierClasses += `${BTN_PREFIX}${keyword} `;
      }
    });
  }

  // convert responsive keywords to class names
  if (config.breakpoints) {
    // check only valid breakpoint properties
    VALID_BREAKPOINT_KEYS.forEach(breakpoint => {
      const keywords = config.breakpoints[breakpoint];
      // convert keywords to full modifier classnames
      if (keywords) {
        keywords.split(' ').forEach(keyword => {
          modifierClasses += `${BTN_PREFIX}${keyword}-at-${breakpoint} `;
        });
      }
    });
  }

  // actionType
  // (for triggering global ui-layers stuff: modals, interstitials, popups, etc)
  if (config.actionType) {
    const {
      type
    } = config.actionType;
    const {
      options
    } = config.actionType;

    // add tracking attribute
    attributes += 'data-bactmtrack ';
    if (options) {
      // add stringified, escaped options as an attribute
      const stringifiedOptions = JSON.stringify(config.actionType.options);
      const escapedOptions = Handlebars.Utils.escapeExpression(stringifiedOptions);
      attributes += `data-options="${escapedOptions}" `;
    }

    // function map to handle the main action types (layer | popup)
    // note: type 'popup' has no specific handling requirements anymore
    const typeSwitch = {
      layer() {
        if (options) {
          if (options.rel) {
            attributes += `rel="${options.rel}" `;
          }

          // add 'layer' subtype-based modifiers
          Object.keys(ACTION_LAYER_MODIFIERS).some(key => {
            if (options.hasOwnProperty(key)) {
              modifierClasses += `${ACTION_LAYER_MODIFIERS[key]} `;
              return true;
            }
            return false;
          });
        }
      }
    };
    if (type) {
      // add type-based modifiers
      if (ACTION_MODIFIERS.hasOwnProperty(type)) {
        modifierClasses += `${ACTION_MODIFIERS[type]} `;
      }
      // execute the function map
      if (typeSwitch[type]) {
        typeSwitch[type]();
      }
    }
  } else if (config.adaText) {
    // adding this in as else to the actionType since it has it's own hidden ada text
    content += `<span class="spa-ada-text ada-hidden">&nbsp;${config.adaText}</span>`;
  }

  // Add rel attr if target='_blank' for <a>.
  if (TAG === 'a') {
    EXISTING_ATTRIBUTE_KEYS.forEach(attr => {
      if (attr === 'target') {
        if (config.attributes && config.attributes[attr] === '_blank') {
          if (config.actionType && config.actionType.options && config.actionType.options.rel) {
            attributes += `rel="${config.actionType.options.rel} noopener noreferrer" `;
          } else {
            attributes += `rel="noopener noreferrer" `;
          }
        }
      }
    });
  }

  // button text icon
  if (config.icon && isTextIcon) {
    const iconTextObj = config.icon;
    if (isObject(iconTextObj) && Object.keys(iconTextObj).length > 0) {
      const iconNameValue = iconTextObj.name ? iconTextObj.name : '';
      const iconPosition = iconTextObj.position ? iconTextObj.position : 'left';
      modifierClasses += `${BTN_TEXT_ICON} ${BTN_TEXT_ICON}--${iconPosition} `;
      if (config.selected) {
        ariaPressed = 'aria-pressed="true"';
        modifierClasses += `${BTN_TEXT_ICON}--selected `;
      }

      // Add badging
      if (config.badging) {
        modifierClasses += ' spa-btn-icon--has-badge';
        config.badging.options = config.badging.options || {};
        config.badging.options.position = 'relative';
        badging = badgingHelper(config.badging);
      }
      const iconDataJcr = `${svgHelper({
        type: 'static',
        iconName: iconNameValue,
        classes: 'spa-btn-text-icon',
        focusable: 'false',
        ariaHidden: true
      })}`;
      if (iconPosition === 'right') {
        contentRight = `${iconDataJcr}`;
        contentLeft = `${badging}`;
      } else {
        contentRight = `${badging}`;
        contentLeft = `${iconDataJcr}`;
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('config.icon should be of object type for button text icon');
    }
  }

  // add required attribute keys to key list (if missing)
  REQUIRED_ATTRIBUTE_KEYS.forEach(attr => {
    if (EXISTING_ATTRIBUTE_KEYS.indexOf(attr) === -1) {
      EXISTING_ATTRIBUTE_KEYS.push(attr);
    }
  });

  // remove role attribute from JCR if present
  EXCLUDE_ROLE_ATTRIBUTE_KEY = EXISTING_ATTRIBUTE_KEYS.filter(val => val.indexOf('role') < 0);

  // build the 'attribute' string
  EXCLUDE_ROLE_ATTRIBUTE_KEY.forEach(attr => {
    let val = config.attributes && config.attributes[attr] ? config.attributes[attr] : undefined;
    if (isButton) {
      if (attr === 'type') {
        // ensure 'type' attribute gets set to an appropriate value (for ada compliance)
        if (VALID_TYPE_ATTRIBUTES.indexOf(val) === -1) {
          val = 'button';
        }
      }
    }

    // if the button link is 'secure', use the secureHost parameter to transform the 'href' attribute
    if (Object.prototype.hasOwnProperty.call(config, 'secure') && config.secure === true) {
      if (!config.attributes || !config.attributes.href) {
        throw new Error(`Error in ${UTILITY_NAME}: 'config.attributes' is missing an 'href' property.`);
      } else if (attr === 'href') {
        if (!/^http.+/i.test(val)) {
          if (!secureHost || typeof secureHost !== 'string') {
            if (typeof spaParams !== 'undefined' && spaParams.siteParams) {
              secureHost = spaParams.siteParams.secureHost;
            } else {
              throw new Error(`Error in ${UTILITY_NAME}: secureHost is not defined and is necessary for secure link button.`);
            }
          }
          val = secureHost + val;
        }
      }
    }

    // append this attribute to the 'attributes' string
    attributes += `${attr}="${val}" `;
  });

  // finalize the 'class' attribute
  const CLASSES = `class="${BASE_CLASS} ${modifierClasses} ${ADDITIONAL_CLASSES}"`;

  // finalize attributes string
  attributes = `${CLASSES} ${attributes}`;

  // adaText

  // finalize output
  const output = `<${TAG} ${config.vueOptionsStr || ''} ${attributes} ${ariaPressed}>${contentLeft}${content}${contentRight}</${TAG}>`;

  // output
  return new Handlebars.SafeString(output);
}

export { button };
